/**
 * CompactPipelineStatus - Compact status line for case lists
 * Shows: 🔍 Classifier AI working (89/127) → 🕵️ Anomaly AI next → 👤 8 items review
 */

import React from 'react';
import { ClaimPipeline, AgentStatus, AI_AGENTS } from './types';
import { Loader2, ArrowRight, User, CheckCircle, AlertCircle, Pause, Bot } from 'lucide-react';

interface CompactPipelineStatusProps {
  pipeline?: ClaimPipeline | null;
  loading?: boolean;
}

export default function CompactPipelineStatus({ pipeline, loading }: CompactPipelineStatusProps) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
        <Loader2 className="w-3 h-3 animate-spin" />
        Loading...
      </div>
    );
  }

  if (!pipeline || !pipeline.agents || pipeline.agents.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
        <Pause className="w-3 h-3" /> No active processing
      </div>
    );
  }
  
  const runningAgent = pipeline.agents.find(a => a.status === 'running');
  const waitingHuman = pipeline.agents.find(a => a.status === 'waiting_human');
  const nextQueued = pipeline.agents.find(a => a.status === 'queued');
  const allComplete = pipeline.agents.every(a => a.status === 'complete' || a.status === 'idle');
  
  // All complete state
  if (allComplete && !runningAgent && !waitingHuman) {
    return (
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-verified)' }}>
        <CheckCircle className="w-3 h-3" />
        <span>AI analysis complete</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      {/* Currently running agent */}
      {runningAgent && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
          {(() => { const AgentIcon = AI_AGENTS[runningAgent.agentId]?.icon || Bot; return <AgentIcon size={12} />; })()}
          <span className="font-medium">{runningAgent.agentName.replace(' AI', '')}</span>
          {runningAgent.progress && (
            <span style={{ color: 'var(--color-needs-human-text)' }}>
              ({runningAgent.progress.current}/{runningAgent.progress.total})
            </span>
          )}
          <Loader2 className="w-3 h-3 animate-spin" />
        </span>
      )}

      {/* Next agent */}
      {nextQueued && runningAgent && (
        <>
          <ArrowRight className="w-3 h-3" style={{ color: 'var(--color-text-dim)' }} />
          <span className="inline-flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
            {(() => { const AgentIcon = AI_AGENTS[nextQueued.agentId]?.icon || Bot; return <AgentIcon size={12} />; })()}
            <span>{nextQueued.agentName.replace(' AI', '')} next</span>
          </span>
        </>
      )}
      
      {/* Human review needed */}
      {(pipeline.pendingHumanReview > 0 || waitingHuman) && (
        <>
          {(runningAgent || nextQueued) && <ArrowRight className="w-3 h-3" style={{ color: 'var(--color-text-dim)' }} />}
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
            <User className="w-3 h-3" />
            <span>{pipeline.pendingHumanReview || 1} need review</span>
          </span>
        </>
      )}

      {/* Error state */}
      {pipeline.agents.some(a => a.status === 'error') && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
          <AlertCircle className="w-3 h-3" />
          <span>Error</span>
        </span>
      )}
    </div>
  );
}
